import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Shield,
  Lock,
  FileText,
  AlertTriangle,
  Scale,
  UserCheck,
  Eye,
  Smartphone,
  Server,
  Copyright,
  Gavel,
  MapPin,
  Layers,
} from "lucide-react";

const SECTIONS = [
  { id: "introduction", title: "1. Introduction", icon: FileText },
  { id: "eligibility", title: "2. Eligibility & Account", icon: UserCheck },
  { id: "ugc-policy", title: "3. User Content & Conduct", icon: Eye },
  { id: "privacy-data", title: "4. Privacy & Data Policy", icon: Lock },
  { id: "gdpr-rights", title: "5. GDPR & Data Rights", icon: Server },
  {
    id: "intellectual-property",
    title: "6. Intellectual Property",
    icon: Copyright,
  },
  { id: "fraud-policy", title: "7. Zero Tolerance Fraud", icon: AlertTriangle },
  { id: "app-permissions", title: "8. App Permissions", icon: Smartphone },
  { id: "app-usage", title: "9. App Usage & Third Parties", icon: Layers },
  { id: "liability", title: "10. Limitation of Liability", icon: Scale },
  { id: "governing-law", title: "11. Governing Law", icon: Gavel },
  { id: "contact", title: "12. Contact Us", icon: Shield },
];

const TermsCondition = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  // smooth scroll handler
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  };

  // intersection observer to update active sidebar link on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -50% 0px" },
    );

    SECTIONS.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-sm font-medium text-slate-400 sm:text-xs">
            Last Updated:{" "}
            <span className="text-slate-900">October 24, 2024</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 py-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-5xl sm:text-4xl font-black tracking-tight mb-4">
            Terms & Policies
          </h1>
          <p className="text-lg text-slate-500 leading-relaxed">
            Please read these terms carefully. By accessing or using our
            services, you agree to be bound by these Terms and our Privacy
            Policy.
          </p>
        </div>

        <div className="flex flex-row sm:flex-col gap-12 relative">
          {/* Sidebar Navigation (Sticky) */}
          <div className="block sm:hidden w-72 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-slate-100 p-2 max-h-[calc(100vh-120px)] overflow-y-auto custom-scrollbar">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                    activeSection === section.id
                      ? "bg-blue-50 text-blue-700 shadow-sm"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <section.icon
                    className={`w-4 h-4 ${activeSection === section.id ? "text-blue-600" : "text-slate-400"}`}
                  />
                  {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-12 pb-24">
            {/* 1. Introduction */}
            <section id="introduction" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                1. Introduction
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                <p className="mb-4">
                  Welcome to <strong>Freshment</strong>, a service provided by{" "}
                  <strong>GradConnect Innovations Private Limited</strong>{" "}
                  ("Company", "we", "us", or "our"). These Terms of Use
                  ("Terms") govern your access to and use of our website, mobile
                  applications, and services (collectively, the "Services").
                </p>
                <p>
                  By registering for an account, accessing the Platform, or
                  using any Service, you agree to be bound by these Terms and
                  our Privacy Policy. If you do not agree to these Terms, you
                  must strictly stop using our Services immediately.
                </p>
              </div>
            </section>

            {/* 2. Eligibility */}
            <section id="eligibility" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                2. Eligibility & Account Security
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                <ul className="list-disc pl-5 space-y-2 mb-4">
                  <li>
                    <strong>Account Registration:</strong> You agree to provide
                    accurate, current, and complete information during the
                    registration process and to update such information to keep
                    it accurate, current, and complete.
                  </li>
                  <li>
                    <strong>Security:</strong> You are responsible for
                    safeguarding the password that you use to access the
                    Service. The Company cannot and will not be liable for any
                    loss or damage arising from your failure to comply with this
                    security obligation.
                  </li>
                  <li>
                    <strong>Account Responsibility:</strong> You are solely
                    responsible for all activities that occur under your
                    account. You must notify us immediately upon becoming aware
                    of any breach of security or unauthorized use of your
                    account.
                  </li>
                </ul>
              </div>
            </section>

            {/* 3. UGC Policy (Crucial for Play Store) */}
            <section id="ugc-policy" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                3. User Content & Conduct (UGC)
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4 font-bold text-slate-800">
                  Freshment allows users to post jobs, upload resumes, and
                  communicate. By using the app, you agree to the following User
                  Generated Content (UGC) policies required for Google Play
                  compliance:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-1 gap-4 mb-4">
                  <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                    <h4 className="font-bold text-red-700 mb-2">
                      Strictly Prohibited:
                    </h4>
                    <ul className="list-disc pl-4 text-sm text-red-600 space-y-1">
                      <li>Hate speech, violence, or bullying.</li>
                      <li>Sexual content or nudity.</li>
                      <li>Fake job postings or spam.</li>
                      <li>Impersonation of others.</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                    <h4 className="font-bold text-emerald-700 mb-2">
                      Our Actions:
                    </h4>
                    <ul className="list-disc pl-4 text-sm text-emerald-600 space-y-1">
                      <li>We moderate content within 24 hours.</li>
                      <li>We ban users who violate these policies.</li>
                      <li>
                        Users can report objectionable content via the "Report"
                        button.
                      </li>
                    </ul>
                  </div>
                </div>
                <p>
                  We reserve the right to remove any content that we determine
                  to be offensive, harmful, or in violation of these Terms or
                  Google Play Policies.
                </p>
              </div>
            </section>

            {/* 4. Privacy & Data */}
            <section id="privacy-data" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                4. Privacy & Data Collection
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  Your privacy is paramount. This section explains what data we
                  collect and why, in accordance with global standards.
                </p>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">
                        Personal Information
                      </h4>
                      <p className="text-sm">
                        We collect Name, Email, Phone Number, and Education
                        details to create your resume profile and match you with
                        jobs.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">
                        Uploaded Documents
                      </h4>
                      <p className="text-sm">
                        Resumes (PDF/Doc) are stored securely and shared ONLY
                        with the Employers you explicitly apply to.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 5. GDPR */}
            <section id="gdpr-rights" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                5. GDPR & Data Rights
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  We respect your right to control your data. Under GDPR and
                  equivalent Indian laws, you have the following rights:
                </p>
                <ul className="list-disc pl-5 space-y-2 mb-6">
                  <li>
                    <strong>Right to Access:</strong> You can request a copy of
                    all data we hold about you.
                  </li>
                  <li>
                    <strong>Right to Rectification:</strong> You can edit your
                    profile at any time.
                  </li>
                  <li>
                    <strong>Right to Deletion (Right to be Forgotten):</strong>{" "}
                    You can delete your account permanently from the Settings
                    menu. Upon deletion, all your data (including resumes) is
                    wiped from our servers immediately.
                  </li>
                </ul>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm">
                  To exercise these rights, email:{" "}
                  <a
                    href="mailto:contact@freshmentapp.com"
                    className="font-bold wrap-anywhere text-slate-900"
                  >
                    contact@freshmentapp.com
                  </a>
                </div>
              </div>
            </section>

            {/* 6. Intellectual Property Rights */}
            <section id="intellectual-property" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                6. Intellectual Property Rights
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  The Service and its original content (excluding Content
                  provided by users), features, and functionality are and will
                  remain the exclusive property of{" "}
                  <strong>GradConnect Innovations Private Limited</strong> and
                  its licensors.
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    All trademarks, logos, and service marks displayed on the
                    Service are our property or the property of other third
                    parties.
                  </li>
                  <li>
                    You are not permitted to use these Marks without our prior
                    written consent or the consent of such third party which may
                    own the Marks.
                  </li>
                  <li>
                    You may not duplicate, copy, or reuse any portion of the
                    HTML/CSS, Javascript, or visual design elements or concepts
                    without express written permission from the Company.
                  </li>
                </ul>
              </div>
            </section>

            {/* 7. Fraud Policy */}
            <section id="fraud-policy" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                7. Zero Tolerance Fraud Policy
              </h2>
              <div className="bg-red-50 p-8 rounded-3xl border border-red-100 shadow-sm text-slate-800 leading-relaxed">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h3 className="text-lg font-bold text-red-700">
                    Money vs. Job Scam
                  </h3>
                </div>
                <p className="mb-4 font-medium">
                  Freshment is a <strong>FREE</strong> platform for candidates.
                  We enforce a strict Zero Tolerance policy:
                </p>
                <ul className="list-disc pl-5 space-y-2 mb-4">
                  <li>
                    Employers are prohibited from asking for money (Security
                    Deposit, Training Fee, Uniform Fee) from candidates.
                  </li>
                  <li>
                    Any Employer found asking for money will be immediately
                    banned and reported to authorities.
                  </li>
                  <li>
                    We do not encourage jobs via consultancies that charge
                    candidates.
                  </li>
                </ul>
                <p className="text-sm text-red-600 font-bold">
                  Report Suspicious Activity:{" "}
                  <a href="mailto:contact@freshmentapp.com">
                    contact@freshmentapp.com
                  </a>
                </p>
              </div>
            </section>

            {/* 8. App Permissions */}
            <section id="app-permissions" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                8. App Permissions
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  To provide our services, the Freshment App may request the
                  following permissions:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-50 pb-2 sm:flex-col sm:items-start sm:gap-1">
                    <span className="font-bold text-slate-900">
                      Camera / Gallery
                    </span>
                    <span className="text-sm">
                      To upload profile pictures or resume images.
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-slate-50 pb-2 sm:flex-col sm:items-start sm:gap-1">
                    <span className="font-bold text-slate-900">Storage</span>
                    <span className="text-sm">To save/upload PDF resumes.</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-slate-50 pb-2 sm:flex-col sm:items-start sm:gap-1">
                    <span className="font-bold text-slate-900">
                      Notifications
                    </span>
                    <span className="text-sm">
                      To send job alerts and interview updates.
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* 9. App Usage & Third Party Services */}
            <section id="app-usage" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                9. App Usage & Third Party Services
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Device Security
                  </h3>
                  <p className="mb-2">
                    Freshment stores and processes personal data that you have
                    provided to us, in order to provide our Service. It's your
                    responsibility to keep your phone and access to the app
                    secure. We therefore recommend that you do not jailbreak or
                    root your phone, which is the process of removing software
                    restrictions and limitations imposed by the official
                    operating system of your device. It could make your phone
                    vulnerable to malware/viruses/malicious programs, compromise
                    your phone's security features and it could mean that the
                    Freshment app won't work properly or at all.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Third Party Services
                  </h3>
                  <p className="mb-2">
                    The app does use third party services that declare their own
                    Terms and Conditions:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-blue-600 font-medium">
                    <li>
                      <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        Google Play Services
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://developers.google.com/admob/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        AdMob
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://firebase.google.com/terms/analytics"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        Google Analytics for Firebase
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://firebase.google.com/terms/crashlytics"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        Firebase Crashlytics
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.facebook.com/legal/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        Facebook
                      </a>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Connectivity & Charges
                  </h3>
                  <p className="mb-2">
                    You should be aware that there are certain things that{" "}
                    <strong>GRADCONNECT INNOVATIONS PRIVATE LIMITED</strong>{" "}
                    will not take responsibility for. Certain functions of the
                    app will require the app to have an active internet
                    connection. The connection can be Wi-Fi, or provided by your
                    mobile network provider, but we cannot take responsibility
                    for the app not working at full functionality if you don't
                    have access to Wi-Fi, and you don't have any of your data
                    allowance left.
                  </p>
                  <p className="mb-2">
                    If you're using the app outside of an area with Wi-Fi, you
                    should remember that your terms of the agreement with your
                    mobile network provider will still apply. As a result, you
                    may be charged by your mobile provider for the cost of data
                    for the duration of the connection while accessing the app,
                    or other third party charges. In using the app, you're
                    accepting responsibility for any such charges, including
                    roaming data charges if you use the app outside of your home
                    territory (i.e. region or country) without turning off data
                    roaming.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Device Responsibility
                  </h3>
                  <p>
                    Along the same lines,{" "}
                    <strong>GRADCONNECT INNOVATIONS PRIVATE LIMITED</strong>{" "}
                    cannot always take responsibility for the way you use the
                    app i.e. You need to make sure that your device stays
                    charged – if it runs out of battery and you can't turn it on
                    to avail the Service, we cannot accept responsibility.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Updates & Termination
                  </h3>
                  <p>
                    At some point, we may wish to update the app. The app is
                    currently available on Android – the requirements for system
                    (and for any additional systems we decide to extend the
                    availability of the app to) may change, and you'll need to
                    download the updates if you want to keep using the app. We
                    do not promise that we will always update the app so that it
                    is relevant to you and/or works with the Android version
                    that you have installed on your device. However, you promise
                    to always accept updates to the application when offered to
                    you. We may also wish to stop providing the app, and may
                    terminate use of it at any time without giving notice of
                    termination to you.
                  </p>
                </div>
              </div>
            </section>

            {/* 10. Limitation of Liability */}
            <section id="liability" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                10. Limitation of Liability
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p>
                  To the maximum extent permitted by law,{" "}
                  <strong>GradConnect Innovations Private Limited</strong> shall
                  not be liable for any indirect, incidental, special,
                  consequential, or punitive damages, or any loss of profits or
                  revenues, whether incurred directly or indirectly, or any loss
                  of data, use, goodwill, or other intangible losses, resulting
                  from (a) your access to or use of or inability to access or
                  use the Service; (b) any conduct or content of any third party
                  on the Service; (c) any content obtained from the Service; or
                  (d) unauthorized access, use, or alteration of your
                  transmissions or content.
                </p>
                <p className="mt-4">
                  With respect to our responsibility for your use of the app,
                  when you're using the app, it's important to bear in mind that
                  although we endeavour to ensure that it is updated and correct
                  at all times, we do rely on third parties to provide
                  information to us so that we can make it available to you. We
                  accept no liability for any loss, direct or indirect, you
                  experience as a result of relying wholly on this functionality
                  of the app.
                </p>
              </div>
            </section>

            {/* 11. Governing Law */}
            <section id="governing-law" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                11. Governing Law
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p>
                  These Terms shall be governed by and construed in accordance
                  with the laws of India, without regard to its conflict of law
                  provisions. Any disputes arising under these terms shall be
                  subject to the exclusive jurisdiction of the courts located in{" "}
                  <strong>Rajkot, Gujarat</strong>.
                </p>
                <p className="mt-4">
                  Our failure to enforce any right or provision of these Terms
                  will not be considered a waiver of those rights. If any
                  provision of these Terms is held to be invalid or
                  unenforceable by a court, the remaining provisions of these
                  Terms will remain in effect.
                </p>
              </div>
            </section>

            {/* 12. Contact */}
            <section id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                12. Contact Us
              </h2>
              <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                  <p className="mb-6 font-medium text-slate-300">
                    If you have any questions about these Terms, the Privacy
                    Policy, or the Service, please contact us.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-1 gap-6">
                    <a
                      href="mailto:contact@freshmentapp.com"
                      className="flex items-center gap-3 bg-white/10 hover:bg-white/20 p-4 rounded-xl transition-colors"
                    >
                      <div className="bg-white text-slate-900 p-2 rounded-lg">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-bold uppercase">
                          Email Support
                        </div>

                        <div
                          className="font-bold "
                          style={{ overflowWrap: "anywhere" }}
                        >
                          contact@freshmentapp.com
                        </div>
                      </div>
                    </a>

                    <div className="flex items-start gap-3 bg-white/10 p-4 rounded-xl">
                      <div className="bg-white text-slate-900 p-2 rounded-lg flex-shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-bold uppercase mb-1">
                          Registered Address
                        </div>
                        <div className="text-sm font-medium leading-relaxed text-slate-200">
                          B-519, RK Iconic Sheetal Park,
                          <br />
                          Puneet Naga Bajrang, Rajkot Bhomeshwar Plot,
                          <br />
                          Rajkot- 360006, Gujarat
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsCondition;
